/**
 * Data Source Management Hook
 * Handles data source loading, state management, and field initialization
 */
import { useEffect, useState, useRef, useCallback } from 'react'
import {
  DataSourceConfig,
  DataSourcesState,
  DataSourceState,
  FormFieldConfig
} from '../types'
import {
  loadDataSource,
  convertToOptions,
  createInitialDataSourceState,
  applyDataSourceTransform
} from '../utils/dataSource'
import { dataSourceCache, generateCacheKey } from '../utils/dataSourceCache'

interface UseDataSourceProps {
  dataSources?: DataSourceConfig[]
  fields: FormFieldConfig[]
  getValues: () => Record<string, any>
  setValue: (name: string, value: any) => void
  initialValues: Record<string, any>
  onDataSourcesReady?: () => void // Add callback for when all data sources are ready
}

interface UseDataSourceReturn {
  dataSourcesState: DataSourcesState
  reloadDataSource: (key: string) => Promise<void>
  getDataSource: (key: string) => DataSourceState | null
}

export const useDataSource = ({
  dataSources = [],
  fields,
  getValues,
  setValue,
  initialValues,
  onDataSourcesReady
}: UseDataSourceProps): UseDataSourceReturn => {
  // Data source state management
  const [dataSourcesState, setDataSourcesState] = useState<DataSourcesState>({})
  const dataSourcesRef = useRef<DataSourcesState>({})

  // Track which fields have had their default values set
  const initializedFieldsRef = useRef<Set<string>>(new Set())
  // Track if callback has been called to prevent multiple calls
  const callbackCalledRef = useRef<boolean>(false)
  // Stash the latest callback so timer/ready effects can read it without
  // depending on its referential identity. Consumers routinely pass inline
  // arrows (new identity every render); depending on them would reset the
  // 1.3s safety-net timer on every parent render and the ready-effect
  // would re-schedule its 50ms microtask repeatedly.
  const onDataSourcesReadyRef = useRef(onDataSourcesReady)
  useEffect(() => {
    onDataSourcesReadyRef.current = onDataSourcesReady
  }, [onDataSourcesReady])

  /**
   * Load data source data and update state
   */
  const loadDataSourceData = useCallback(
    async (dataSource: DataSourceConfig) => {
      const currentState =
        dataSourcesRef.current[dataSource.key] || createInitialDataSourceState()

      // Update loading state
      setDataSourcesState(prev => ({
        ...prev,
        [dataSource.key]: {
          ...currentState,
          loading: true,
          error: null
        }
      }))

      try {
        // Generate cache key and load data
        const cacheKey = generateCacheKey(dataSource, getValues())
        const rawData = await dataSourceCache.getOrAdd(cacheKey, () => {
          return loadDataSource(dataSource, getValues())
        })

        // Apply data transformation after caching
        const data = applyDataSourceTransform(dataSource, rawData)

        // Convert to options format
        const fieldConfig = getDataSourceFieldConfig(dataSource.key)
        const options = convertToOptions(
          data,
          fieldConfig?.valueField || 'value',
          fieldConfig?.labelField || 'label'
        )

        // Update data source state
        const newState = {
          loading: false,
          data,
          error: null,
          options
        }

        setDataSourcesState(prev => ({
          ...prev,
          [dataSource.key]: newState
        }))

        dataSourcesRef.current = {
          ...dataSourcesRef.current,
          [dataSource.key]: newState
        }

        // Initialize fields that depend on this data source
        initializeFieldsWithDataSource(dataSource.key, options)

        return data
      } catch (error) {
        // Handle error state - clear data and options when error occurs
        const errorState = {
          loading: false,
          data: null,
          options: [],
          error
        }

        setDataSourcesState(prev => ({
          ...prev,
          [dataSource.key]: errorState
        }))

        dataSourcesRef.current = {
          ...dataSourcesRef.current,
          [dataSource.key]: errorState
        }

        return null
      }
    },
    [getValues]
  )

  /**
   * Get field config that uses specific data source
   */
  const getDataSourceFieldConfig = useCallback(
    (dataSourceKey: string): FormFieldConfig | undefined => {
      return fields.find(field => field.dataSource === dataSourceKey)
    },
    [fields]
  )

  /**
   * Initialize fields with data source after it's loaded
   */
  const initializeFieldsWithDataSource = useCallback(
    (dataSourceKey: string, options: any[]) => {
      const fieldsUsingDataSource = fields.filter(
        field => field.dataSource === dataSourceKey
      )

      fieldsUsingDataSource.forEach(field => {
        const fieldName = field.name
        const currentValue = getValues()[fieldName]

        // Skip if already initialized, unless it's an auto-select field that was cleared
        if (
          initializedFieldsRef.current.has(fieldName) &&
          !(field.autoSelectFirst && !currentValue)
        ) {
          return
        }

        const hasInitialValue = fieldName in initialValues
        const initialValue = initialValues[fieldName]

        // Auto select first option if configured and no current value
        if (field.autoSelectFirst && options.length > 0 && !currentValue) {
          setValue(fieldName, options[0].value)
          initializedFieldsRef.current.add(fieldName)
          return
        }

        // Handle existing initial values
        if (hasInitialValue) {
          // Check if initial value exists in options
          const valueExists = options.some(
            option => option.value === initialValue
          )

          if (valueExists) {
            setValue(fieldName, initialValue)
          } else if (initialValue) {
            console.warn(
              `[FormBuilder] Initial value "${initialValue}" for field "${fieldName}" not found in data source "${dataSourceKey}"`
            )
          }

          initializedFieldsRef.current.add(fieldName)
        }
      })
    },
    [fields, initialValues, setValue, getValues]
  )

  /**
   * Manual reload data source
   */
  const reloadDataSource = useCallback(
    async (key: string) => {
      const dataSource = dataSources.find(ds => ds.key === key)
      if (dataSource) {
        // Clear cache to ensure fresh data
        const cacheKey = generateCacheKey(dataSource, getValues())
        dataSourceCache.clearKey(cacheKey)

        await loadDataSourceData(dataSource)
      }
    },
    [dataSources, getValues, loadDataSourceData]
  )

  /**
   * Get data source state by key
   */
  const getDataSource = useCallback((key: string): DataSourceState | null => {
    return dataSourcesRef.current[key] || null
  }, [])

  /**
   * Check if all auto-loading data sources are ready
   */
  const checkAllDataSourcesReady = useCallback(() => {
    if (dataSources.length === 0) return true

    const autoLoadDataSources = dataSources.filter(ds => ds.autoLoad !== false)
    if (autoLoadDataSources.length === 0) return true

    return autoLoadDataSources.every(ds => {
      const state = dataSourcesRef.current[ds.key]
      return state && !state.loading && state.data !== null && !state.error
    })
  }, [dataSources])

  /**
   * Trigger onDataSourcesReady callback when all data sources are ready.
   * Callback resolved via ref so inline-arrow consumers don't retrigger
   * this effect every render.
   */
  useEffect(() => {
    if (
      onDataSourcesReadyRef.current &&
      !callbackCalledRef.current &&
      checkAllDataSourcesReady()
    ) {
      callbackCalledRef.current = true
      // Use a small delay to ensure all state updates are completed
      setTimeout(() => {
        onDataSourcesReadyRef.current?.()
      }, 50)
    }
  }, [dataSourcesState, checkAllDataSourcesReady])

  /**
   * Safety-net: guarantee onDataSourcesReady fires within 1.3s of mount even
   * if some data source never satisfies the "loaded" predicate above (e.g. a
   * request 500s, a transform returns null, or an inflight promise stalls).
   * Otherwise the consumer's gating UI (Spin, disabled Save button, etc.)
   * would stay locked indefinitely on any single failure.
   *
   * Mount-only (empty deps) + callback resolved via ref so parent re-renders
   * don't reset the timeout.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!callbackCalledRef.current) {
        callbackCalledRef.current = true
        onDataSourcesReadyRef.current?.()
      }
    }, 1300)
    return () => clearTimeout(timer)
  }, [])

  // Initialize data sources on mount
  useEffect(() => {
    if (dataSources.length === 0) return

    // Initialize data source states
    const initialDataSources: DataSourcesState = {}
    dataSources.forEach(dataSource => {
      initialDataSources[dataSource.key] = createInitialDataSourceState()
    })

    setDataSourcesState(initialDataSources)
    dataSourcesRef.current = initialDataSources

    // Load auto-loading data sources
    const loadInitialDataSources = async () => {
      for (const dataSource of dataSources) {
        if (dataSource.autoLoad !== false) {
          await loadDataSourceData(dataSource)
        }
      }
    }

    loadInitialDataSources()

    // Cleanup cache on unmount
    return () => {
      dataSourceCache.clear()
    }
  }, [dataSources, loadDataSourceData])

  return {
    dataSourcesState,
    reloadDataSource,
    getDataSource
  }
}
